import Typography from "@common/components/Typography";
import Grid from "@mui/material/Grid2";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTestId } from "@common/utils/getTestId";

const composeTestIds = (label: DeckboardInformationListValue["label"]) => {
  if (typeof label !== "string") {
    return {
      labelTestId: undefined,
      valueTestId: undefined,
    };
  }

  const rootId = getTestId(label);

  return {
    labelTestId: `${rootId}-label`,
    valueTestId: `${rootId}-value`,
  };
};

export type DeckboardInformationListValue = {
  label: string | ReactNode;
  value?: string | number | ReactNode;
  link?: string;
  password?: boolean;
};

type DeckboardInformationListProps = {
  deckboardInformationValue?: DeckboardInformationListValue[];
};
const DeckboardInformationList = (props: DeckboardInformationListProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { deckboardInformationValue } = props;
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const displayDeckboardInformation = () => {
    return deckboardInformationValue?.map(
      ({ label, value, link, password }, index) => {
        const { labelTestId, valueTestId } = composeTestIds(label);

        return (
          <Box
            key={index}
            sx={(theme) => ({
              borderBottom: `1px dashed ${theme.palette.grey[800]}`,
              paddingBottom: theme.spacing(0.5),
              marginBottom: theme.spacing(0.5),
              "& .MuiTypography-root": {
                display: "flex",
                color: theme.palette.grey[400],
                alignItems: "baseline",
              },
            })}
          >
            <Grid
              container
              sx={{
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {typeof label === "string" ? (
                <Typography component="span" sx={{ whiteSpace: "nowrap" }} data-testid={labelTestId}>
                  {t(label)}
                </Typography>
              ) : (
                label
              )}

              <Typography
                reserveSpace
                component="span"
                size="medium"
                realTime
                sx={(theme) => ({
                  "&.MuiTypography-root": {
                    color: theme.palette.success.main,
                    justifyContent: "end",
                    flex: 1,
                    textAlign: "end",
                    wordBreak: "break-word",
                    maxWidth: "20rem",
                    alignItems: "center",
                    fontWeight: 600,
                  },
                })}
                data-testid={valueTestId}
              >
                {password ? (
                  <>
                    <span style={{ marginRight: "1rem" }}>
                      {showPassword ? value : "******"}
                    </span>
                    <Tooltip
                      title={
                        value === undefined ? t("Account security enabled") : ""
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          sx={{
                            color: (theme) => theme.palette.common.white,
                          }}
                          disabled={value === undefined}
                          size="small"
                          color="inherit"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                ) : link ? (
                  <a
                    href={link}
                    style={{ color: theme.palette.success.main }}
                    target="_blank"
                  >
                    {value}
                  </a>
                ) : (
                  value
                )}
              </Typography>
            </Grid>
          </Box>
        );
      }
    );
  };
  return (
    <Box
      sx={(theme) => ({
        "& $deckboardData:last-child": {
          borderBottom: "none",
          marginBottom: theme.spacing(1),
        },
      })}
    >
      {displayDeckboardInformation()}
    </Box>
  );
};

export default DeckboardInformationList;
